import { hexToRgb, hexToPptx } from './exportDefaults';

// Format number with commas
const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString();
};

// Generate PDF export
export const generatePDF = async ({ config, data, pageType, dateRange, toast }) => {
  try {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const primaryColor = hexToRgb(config.colors.primary);
    const accentColor = hexToRgb(config.colors.accent);
    const greenColor = [16, 185, 129];
    const cyanColor = [6, 182, 212];
    const darkColor = [26, 32, 44];

    const companyName = config.branding.companyName || 'StatFalcon';
    const title = config.text.title || getDefaultTitle(pageType);
    const subtitle = config.text.subtitle || getDefaultSubtitle(pageType);
    const footerText = config.text.footerText || `Generated by ${companyName}`;

    const addPageBranding = (pageNum, showHeader = true) => {
      if (showHeader) {
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 20, 'F');
        doc.setFillColor(...accentColor);
        doc.rect(0, 20, pageWidth, 3, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(companyName.toUpperCase(), 14, 13);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(subtitle, pageWidth - 14, 13, { align: 'right' });
      }
      doc.setFillColor(248, 250, 252);
      doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
      doc.setDrawColor(230, 230, 230);
      doc.line(0, pageHeight - 12, pageWidth, pageHeight - 12);
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(7);
      doc.text(`${footerText} | ${new Date().toLocaleString()}`, 14, pageHeight - 5);
      doc.text(`Page ${pageNum}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
      doc.text(String(dateRange || 'All Time'), pageWidth - 14, pageHeight - 5, { align: 'right' });
    };

    const drawMetricCard = (x, y, width, height, label, value, color) => {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...color);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, 2, 2, 'FD');
      doc.setFillColor(...color);
      doc.rect(x, y, 4, height, 'F');
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(String(label).toUpperCase(), x + 8, y + 8);
      doc.setTextColor(...color);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(value), x + 8, y + 18);
    };

    const drawSectionHeader = (y, sectionTitle, color) => {
      doc.setFillColor(...color);
      doc.roundedRect(14, y, 4, 12, 1, 1, 'F');
      doc.setTextColor(...darkColor);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(sectionTitle, 22, y + 9);
      return y + 16;
    };

    // Cover page
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    for (let i = 0; i < 6; i++) {
      doc.setFillColor(255, 255, 255, 0.03);
      doc.circle(pageWidth * 0.8, pageHeight * 0.3, 30 + i * 20, 'F');
    }
    doc.setFillColor(...accentColor);
    doc.rect(0, pageHeight * 0.55, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(42);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName.toUpperCase(), 20, 60);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 20, 78);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(20, 100, 170, 50, 3, 3, 'F');
    doc.setTextColor(...darkColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORT PERIOD', 30, 115);
    doc.setFontSize(14);
    doc.text(String(dateRange || 'All Time'), 30, 130);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, 30, 142);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(subtitle, 20, pageHeight * 0.55 + 16);
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text(`Powered by ${companyName} Analytics Platform`, 20, pageHeight - 20);

    // Page 2: Executive Summary
    doc.addPage();
    addPageBranding(1);
    let yPos = 32;
    yPos = drawSectionHeader(yPos, 'Executive Summary', primaryColor);

    const cardWidth = 43;
    const cardHeight = 28;
    const cardGap = 3;
    const startX = 14;

    // Build metrics based on page type
    const metrics = buildMetrics(data, pageType, { primaryColor, accentColor, greenColor, cyanColor });
    metrics.forEach((m, i) => {
      drawMetricCard(startX + i * (cardWidth + cardGap), yPos, cardWidth, cardHeight, m.label, m.value, m.color);
    });
    yPos += cardHeight + 12;

    // Key insights
    const insights = buildInsights(data, pageType);
    if (insights.length > 0) {
      doc.setFillColor(250, 250, 255);
      doc.roundedRect(14, yPos, pageWidth - 28, 10 + insights.length * 8, 2, 2, 'F');
      doc.setTextColor(...darkColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Key Insights', 20, yPos + 10);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      insights.forEach((insight, i) => {
        doc.setFillColor(...primaryColor);
        doc.circle(22, yPos + 18 + i * 8, 1.5, 'F');
        doc.text(String(insight), 27, yPos + 19 + i * 8);
      });
      yPos += 18 + insights.length * 8 + 10;
    }

    // Page-specific tables
    if (pageType === 'reports' && data.campaignData?.length > 0) {
      yPos = drawSectionHeader(yPos, 'Campaign Performance', accentColor);
      const campaignTableData = data.campaignData.slice(0, 12).map((c, i) => [
        String(i + 1),
        c.name || '',
        formatNumber(c.impressions || 0),
        formatNumber(c.clicks || 0),
        `${c.ctr || 0}%`,
      ]);
      autoTable(doc, {
        startY: yPos,
        head: [['#', 'Campaign Name', 'Impressions', 'Clicks', 'CTR']],
        body: campaignTableData,
        theme: 'plain',
        headStyles: { fillColor: [250, 245, 255], textColor: darkColor, fontStyle: 'bold', fontSize: 8, cellPadding: 4 },
        bodyStyles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center', fontStyle: 'bold', textColor: accentColor },
          1: { cellWidth: 70 },
          2: { cellWidth: 28, halign: 'right' },
          3: { cellWidth: 22, halign: 'right' },
          4: { cellWidth: 18, halign: 'right', fontStyle: 'bold' },
        },
        margin: { left: 14, right: 14 },
        willDrawCell: (cellData) => {
          if (cellData.section === 'body' && cellData.row.index % 2 === 1) {
            doc.setFillColor(252, 252, 254);
            doc.rect(cellData.cell.x, cellData.cell.y, cellData.cell.width, cellData.cell.height, 'F');
          }
        },
      });
    }

    if (pageType === 'reports' && data.zoneData?.length > 0) {
      doc.addPage();
      addPageBranding(2);
      let zoneY = 32;
      zoneY = drawSectionHeader(zoneY, 'Zone Performance', greenColor);
      const zoneTableData = data.zoneData.slice(0, 12).map(z => [
        z.name || '',
        z.site || '',
        z.size || '',
        formatNumber(z.impressions || 0),
        formatNumber(z.clicks || 0),
        `${z.ctr || 0}%`,
      ]);
      autoTable(doc, {
        startY: zoneY,
        head: [['Zone Name', 'Site', 'Size', 'Impressions', 'Clicks', 'CTR']],
        body: zoneTableData,
        theme: 'plain',
        headStyles: { fillColor: [240, 253, 244], textColor: darkColor, fontStyle: 'bold', fontSize: 8, cellPadding: 4 },
        bodyStyles: { fontSize: 8, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
    }

    if (pageType === 'analytics' && data.pagesData?.length > 0) {
      yPos = drawSectionHeader(yPos, 'Top Pages', greenColor);
      const pagesTableData = data.pagesData.slice(0, 10).map(p => [
        p.path || '',
        formatNumber(p.views || 0),
        `${p.percentage || 0}%`,
      ]);
      autoTable(doc, {
        startY: yPos,
        head: [['Page Path', 'Views', 'Share']],
        body: pagesTableData,
        theme: 'plain',
        headStyles: { fillColor: [240, 253, 244], textColor: darkColor, fontStyle: 'bold', fontSize: 8, cellPadding: 4 },
        bodyStyles: { fontSize: 8, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
    }

    if (pageType === 'dashboard' && data.trafficData?.length > 0) {
      yPos = drawSectionHeader(yPos, 'Traffic Overview', greenColor);
      const trafficTableData = data.trafficData.slice(0, 14).map(t => [
        t.date || '',
        formatNumber(t.visitors || 0),
        formatNumber(t.pageviews || 0),
      ]);
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Visitors', 'Pageviews']],
        body: trafficTableData,
        theme: 'plain',
        headStyles: { fillColor: [240, 253, 244], textColor: darkColor, fontStyle: 'bold', fontSize: 8, cellPadding: 4 },
        bodyStyles: { fontSize: 8, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
    }

    doc.save(`${companyName.toLowerCase().replace(/\s+/g, '_')}_report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast?.({ title: 'PDF exported successfully', status: 'success', duration: 3000 });
  } catch (err) {
    console.error('PDF export error:', err);
    toast?.({ title: 'PDF export failed', description: err.message, status: 'error', duration: 5000 });
  }
};

// Generate PowerPoint export
export const generatePowerPoint = async ({ config, data, pageType, dateRange, toast }) => {
  try {
    const PptxGenJS = await import('pptxgenjs');
    const pptx = new PptxGenJS.default();

    const companyName = config.branding.companyName || 'StatFalcon';
    const title = config.text.title || getDefaultTitle(pageType);

    pptx.author = companyName;
    pptx.title = title;
    pptx.subject = 'Analytics Report';
    pptx.company = companyName;

    const primaryColor = hexToPptx(config.colors.primary);
    const accentColor = hexToPptx(config.colors.accent);
    const greenColor = '10B981';
    const cyanColor = '06B6D4';
    const darkColor = '1A202C';

    const dateRangeStr = String(dateRange || 'All Time');

    const addSlideFooter = (slide, slideNum, total) => {
      slide.addShape(pptx.ShapeType.rect, { x: 0, y: 5.1, w: '100%', h: 0.4, fill: { color: 'F1F5F9' } });
      slide.addText(companyName.toUpperCase(), { x: 0.3, y: 5.18, w: 2, h: 0.25, fontSize: 8, bold: true, color: primaryColor, fontFace: 'Arial' });
      slide.addText(`${slideNum} / ${total}`, { x: 4.2, y: 5.18, w: 1.6, h: 0.25, fontSize: 8, color: '666666', align: 'center', fontFace: 'Arial' });
      slide.addText(dateRangeStr, { x: 7, y: 5.18, w: 2.7, h: 0.25, fontSize: 8, color: '666666', align: 'right', fontFace: 'Arial' });
    };

    // Calculate total slides
    let totalSlides = 2; // Title + Summary
    if (pageType === 'reports') {
      if (data.campaignData?.length > 0) totalSlides++;
      if (data.zoneData?.length > 0) totalSlides++;
      if (data.adData?.length > 0) totalSlides++;
    } else if (pageType === 'analytics') {
      if (data.pagesData?.length > 0) totalSlides++;
      if (data.sourcesData?.length > 0) totalSlides++;
    } else if (pageType === 'dashboard') {
      totalSlides++; // Traffic data
    }
    totalSlides++; // Thank you slide

    // Title Slide
    const slide1 = pptx.addSlide();
    slide1.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: '100%', h: '100%', fill: { color: primaryColor } });
    slide1.addShape(pptx.ShapeType.ellipse, { x: 6, y: -1.5, w: 5, h: 5, fill: { color: lightenColor(primaryColor), transparency: 60 } });
    slide1.addShape(pptx.ShapeType.ellipse, { x: 7, y: 3, w: 4, h: 4, fill: { color: lightenColor(primaryColor), transparency: 70 } });
    slide1.addShape(pptx.ShapeType.rect, { x: 0, y: 3.8, w: '100%', h: 0.15, fill: { color: accentColor } });
    slide1.addText(companyName.toUpperCase(), { x: 0.6, y: 1.4, w: 8, h: 0.8, fontSize: 48, bold: true, color: 'FFFFFF', fontFace: 'Arial' });
    slide1.addText(title, { x: 0.6, y: 2.2, w: 8, h: 0.5, fontSize: 24, color: 'FFFFFF', fontFace: 'Arial' });
    slide1.addShape(pptx.ShapeType.roundRect, { x: 0.6, y: 4.3, w: 4, h: 0.9, fill: { color: 'FFFFFF' }, rectRadius: 0.1 });
    slide1.addText('REPORT PERIOD', { x: 0.8, y: 4.4, w: 3.6, h: 0.3, fontSize: 9, bold: true, color: '666666', fontFace: 'Arial' });
    slide1.addText(dateRangeStr, { x: 0.8, y: 4.7, w: 3.6, h: 0.4, fontSize: 14, bold: true, color: darkColor, fontFace: 'Arial' });
    slide1.addText(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), { x: 5, y: 4.6, w: 4.5, h: 0.4, fontSize: 12, color: 'FFFFFF', align: 'right', fontFace: 'Arial' });

    // Executive Summary Slide
    let currentSlide = 1;
    const slide2 = pptx.addSlide();
    currentSlide++;
    slide2.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 1, fill: { color: primaryColor } });
    slide2.addShape(pptx.ShapeType.rect, { x: 0, y: 0.95, w: 2, h: 0.1, fill: { color: accentColor } });
    slide2.addText('Executive Summary', { x: 0.4, y: 0.3, w: 9, h: 0.5, fontSize: 26, bold: true, color: 'FFFFFF', fontFace: 'Arial' });
    addSlideFooter(slide2, currentSlide, totalSlides);

    // Metrics for summary
    const metrics = buildPptMetrics(data, pageType, { primaryColor, accentColor, greenColor, cyanColor });
    metrics.forEach((metric, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = 0.4 + col * 4.8;
      const y = 1.2 + row * 1.5;
      slide2.addShape(pptx.ShapeType.roundRect, { x, y, w: 4.5, h: 1.3, fill: { color: metric.bgColor }, rectRadius: 0.1 });
      slide2.addShape(pptx.ShapeType.rect, { x, y, w: 0.12, h: 1.3, fill: { color: metric.color } });
      slide2.addText(String(metric.label), { x: x + 0.3, y: y + 0.15, w: 4, h: 0.3, fontSize: 10, color: '666666', fontFace: 'Arial' });
      slide2.addText(String(metric.value), { x: x + 0.3, y: y + 0.5, w: 4, h: 0.6, fontSize: 32, bold: true, color: metric.color, fontFace: 'Arial' });
    });

    // Key insight box
    const insight = buildTopInsight(data, pageType);
    slide2.addShape(pptx.ShapeType.roundRect, { x: 0.4, y: 4.3, w: 9.2, h: 0.7, fill: { color: 'FFFBEB' }, rectRadius: 0.08 });
    slide2.addText('KEY INSIGHT', { x: 0.6, y: 4.38, w: 1.5, h: 0.2, fontSize: 8, bold: true, color: 'B45309', fontFace: 'Arial' });
    slide2.addText(String(insight), { x: 0.6, y: 4.58, w: 8.8, h: 0.35, fontSize: 11, color: '78350F', fontFace: 'Arial' });

    // Data slides based on page type
    if (pageType === 'reports' && data.campaignData?.length > 0) {
      const slide3 = pptx.addSlide();
      currentSlide++;
      slide3.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 1, fill: { color: accentColor } });
      slide3.addShape(pptx.ShapeType.rect, { x: 0, y: 0.95, w: 2, h: 0.1, fill: { color: primaryColor } });
      slide3.addText('Campaign Performance', { x: 0.4, y: 0.3, w: 9, h: 0.5, fontSize: 26, bold: true, color: 'FFFFFF', fontFace: 'Arial' });
      addSlideFooter(slide3, currentSlide, totalSlides);

      const campaignTableData = [
        [
          { text: '#', options: { bold: true, fill: accentColor, color: 'FFFFFF', align: 'center' } },
          { text: 'Campaign Name', options: { bold: true, fill: accentColor, color: 'FFFFFF' } },
          { text: 'Impressions', options: { bold: true, fill: accentColor, color: 'FFFFFF', align: 'right' } },
          { text: 'Clicks', options: { bold: true, fill: accentColor, color: 'FFFFFF', align: 'right' } },
          { text: 'CTR', options: { bold: true, fill: accentColor, color: 'FFFFFF', align: 'right' } },
        ],
        ...data.campaignData.slice(0, 6).map((c, i) => {
          const rowBg = i % 2 ? 'FDF2F8' : 'FFFFFF';
          return [
            { text: String(i + 1), options: { fill: rowBg, color: accentColor, bold: true, align: 'center' } },
            { text: c.name || '', options: { fill: rowBg, color: darkColor } },
            { text: formatNumber(c.impressions || 0), options: { fill: rowBg, color: '374151', align: 'right' } },
            { text: formatNumber(c.clicks || 0), options: { fill: rowBg, color: '374151', align: 'right' } },
            { text: `${c.ctr || 0}%`, options: { fill: rowBg, color: parseFloat(c.ctr) >= 1 ? greenColor : accentColor, bold: true, align: 'right' } },
          ];
        }),
      ];

      slide3.addTable(campaignTableData, {
        x: 0.4, y: 1.2, w: 9.2,
        colW: [0.5, 4.2, 1.6, 1.4, 1.0],
        rowH: 0.45,
        fontSize: 11,
        fontFace: 'Arial',
        border: { type: 'solid', pt: 0.5, color: 'E5E7EB' },
      });
    }

    if (pageType === 'reports' && data.zoneData?.length > 0) {
      const slide4 = pptx.addSlide();
      currentSlide++;
      slide4.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 1, fill: { color: greenColor } });
      slide4.addShape(pptx.ShapeType.rect, { x: 0, y: 0.95, w: 2, h: 0.1, fill: { color: accentColor } });
      slide4.addText('Zone Performance', { x: 0.4, y: 0.3, w: 9, h: 0.5, fontSize: 26, bold: true, color: 'FFFFFF', fontFace: 'Arial' });
      addSlideFooter(slide4, currentSlide, totalSlides);

      const zoneTableData = [
        [
          { text: 'Zone Name', options: { bold: true, fill: greenColor, color: 'FFFFFF' } },
          { text: 'Site', options: { bold: true, fill: greenColor, color: 'FFFFFF' } },
          { text: 'Impressions', options: { bold: true, fill: greenColor, color: 'FFFFFF', align: 'right' } },
          { text: 'Clicks', options: { bold: true, fill: greenColor, color: 'FFFFFF', align: 'right' } },
          { text: 'CTR', options: { bold: true, fill: greenColor, color: 'FFFFFF', align: 'right' } },
        ],
        ...data.zoneData.slice(0, 6).map((z, i) => {
          const rowBg = i % 2 ? 'ECFDF5' : 'FFFFFF';
          return [
            { text: z.name || '', options: { fill: rowBg, color: darkColor } },
            { text: z.site || '', options: { fill: rowBg, color: '6B7280' } },
            { text: formatNumber(z.impressions || 0), options: { fill: rowBg, color: '374151', align: 'right' } },
            { text: formatNumber(z.clicks || 0), options: { fill: rowBg, color: '374151', align: 'right' } },
            { text: `${z.ctr || 0}%`, options: { fill: rowBg, color: parseFloat(z.ctr) >= 1 ? greenColor : '374151', bold: true, align: 'right' } },
          ];
        }),
      ];

      slide4.addTable(zoneTableData, {
        x: 0.4, y: 1.2, w: 9.2,
        colW: [2.8, 2.6, 1.5, 1.3, 1.0],
        rowH: 0.45,
        fontSize: 11,
        fontFace: 'Arial',
        border: { type: 'solid', pt: 0.5, color: 'E5E7EB' },
      });
    }

    if (pageType === 'analytics' && data.pagesData?.length > 0) {
      const slidePages = pptx.addSlide();
      currentSlide++;
      slidePages.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 1, fill: { color: greenColor } });
      slidePages.addShape(pptx.ShapeType.rect, { x: 0, y: 0.95, w: 2, h: 0.1, fill: { color: accentColor } });
      slidePages.addText('Top Pages', { x: 0.4, y: 0.3, w: 9, h: 0.5, fontSize: 26, bold: true, color: 'FFFFFF', fontFace: 'Arial' });
      addSlideFooter(slidePages, currentSlide, totalSlides);

      const pagesTableData = [
        [
          { text: 'Page Path', options: { bold: true, fill: greenColor, color: 'FFFFFF' } },
          { text: 'Views', options: { bold: true, fill: greenColor, color: 'FFFFFF', align: 'right' } },
          { text: 'Share', options: { bold: true, fill: greenColor, color: 'FFFFFF', align: 'right' } },
        ],
        ...data.pagesData.slice(0, 8).map((p, i) => {
          const rowBg = i % 2 ? 'ECFDF5' : 'FFFFFF';
          return [
            { text: p.path || '', options: { fill: rowBg, color: darkColor } },
            { text: formatNumber(p.views || 0), options: { fill: rowBg, color: '374151', align: 'right' } },
            { text: `${p.percentage || 0}%`, options: { fill: rowBg, color: '374151', align: 'right' } },
          ];
        }),
      ];

      slidePages.addTable(pagesTableData, {
        x: 0.4, y: 1.2, w: 9.2,
        colW: [6, 1.6, 1.6],
        rowH: 0.45,
        fontSize: 11,
        fontFace: 'Arial',
        border: { type: 'solid', pt: 0.5, color: 'E5E7EB' },
      });
    }

    // Thank You Slide
    const slideEnd = pptx.addSlide();
    slideEnd.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: '100%', h: '100%', fill: { color: darkColor } });
    slideEnd.addShape(pptx.ShapeType.ellipse, { x: -2, y: 3, w: 6, h: 6, fill: { color: '2D3748', transparency: 50 } });
    slideEnd.addShape(pptx.ShapeType.ellipse, { x: 7, y: -1, w: 4, h: 4, fill: { color: '374151', transparency: 50 } });
    slideEnd.addShape(pptx.ShapeType.rect, { x: 3.5, y: 2.3, w: 3, h: 0.08, fill: { color: primaryColor } });
    slideEnd.addText('Thank You', { x: 0, y: 1.6, w: '100%', h: 0.8, fontSize: 44, bold: true, color: 'FFFFFF', align: 'center', fontFace: 'Arial' });
    slideEnd.addText('Questions or feedback?', { x: 0, y: 2.6, w: '100%', h: 0.5, fontSize: 18, color: '9CA3AF', align: 'center', fontFace: 'Arial' });
    slideEnd.addShape(pptx.ShapeType.roundRect, { x: 2.5, y: 3.4, w: 5, h: 0.8, fill: { color: primaryColor }, rectRadius: 0.1 });
    slideEnd.addText(`Powered by ${companyName}`, { x: 2.5, y: 3.55, w: 5, h: 0.5, fontSize: 14, bold: true, color: 'FFFFFF', align: 'center', fontFace: 'Arial' });
    slideEnd.addText(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' }), { x: 0, y: 4.6, w: '100%', h: 0.4, fontSize: 10, color: '6B7280', align: 'center', fontFace: 'Arial' });

    await pptx.writeFile({ fileName: `${companyName.toLowerCase().replace(/\s+/g, '_')}_presentation_${new Date().toISOString().split('T')[0]}.pptx` });
    toast?.({ title: 'PowerPoint exported successfully', status: 'success', duration: 3000 });
  } catch (err) {
    console.error('PowerPoint export error:', err);
    toast?.({ title: 'PowerPoint export failed', description: err.message, status: 'error', duration: 5000 });
  }
};

// Helper functions
const getDefaultTitle = (pageType) => {
  const titles = { dashboard: 'Dashboard Report', analytics: 'Web Analytics Report', reports: 'Ad Performance Report' };
  return titles[pageType] || 'Report';
};

const getDefaultSubtitle = (pageType) => {
  const subtitles = { dashboard: 'Overview of key performance metrics', analytics: 'Traffic and engagement insights', reports: 'Campaign and ad analytics' };
  return subtitles[pageType] || 'Generated report';
};

const buildMetrics = (data, pageType, colors) => {
  const { primaryColor, accentColor, greenColor, cyanColor } = colors;
  if (pageType === 'reports') {
    return [
      { label: 'Impressions', value: formatNumber(data.overview?.totalImpressions || 0), color: primaryColor },
      { label: 'Clicks', value: formatNumber(data.overview?.totalClicks || 0), color: greenColor },
      { label: 'CTR', value: `${data.overview?.ctr || 0}%`, color: accentColor },
      { label: 'Visitors', value: formatNumber(data.overview?.uniqueVisitors || 0), color: cyanColor },
    ];
  }
  if (pageType === 'analytics') {
    return [
      { label: 'Visitors', value: formatNumber(data.overview?.uniqueVisitors || 0), color: primaryColor },
      { label: 'Sessions', value: formatNumber(data.overview?.totalVisits || 0), color: greenColor },
      { label: 'Page Views', value: formatNumber(data.overview?.totalPageviews || 0), color: accentColor },
      { label: 'Bounce Rate', value: `${data.overview?.bounceRate || 0}%`, color: cyanColor },
    ];
  }
  // Dashboard
  return [
    { label: 'Visitors', value: formatNumber(data.overview?.uniqueVisitors || 0), color: primaryColor },
    { label: 'Page Views', value: formatNumber(data.overview?.totalPageviews || 0), color: greenColor },
    { label: 'Impressions', value: formatNumber(data.overview?.adImpressions || 0), color: accentColor },
    { label: 'CTR', value: `${data.overview?.ctr || 0}%`, color: cyanColor },
  ];
};

const buildPptMetrics = (data, pageType, colors) => {
  const { primaryColor, accentColor, greenColor, cyanColor } = colors;
  if (pageType === 'reports') {
    return [
      { label: 'TOTAL IMPRESSIONS', value: formatNumber(data.overview?.totalImpressions || 0), color: primaryColor, bgColor: 'EEF2FF' },
      { label: 'TOTAL CLICKS', value: formatNumber(data.overview?.totalClicks || 0), color: greenColor, bgColor: 'ECFDF5' },
      { label: 'CLICK-THROUGH RATE', value: `${data.overview?.ctr || 0}%`, color: accentColor, bgColor: 'FDF2F8' },
      { label: 'UNIQUE VISITORS', value: formatNumber(data.overview?.uniqueVisitors || 0), color: cyanColor, bgColor: 'ECFEFF' },
    ];
  }
  if (pageType === 'analytics') {
    return [
      { label: 'UNIQUE VISITORS', value: formatNumber(data.overview?.uniqueVisitors || 0), color: primaryColor, bgColor: 'EEF2FF' },
      { label: 'TOTAL SESSIONS', value: formatNumber(data.overview?.totalVisits || 0), color: greenColor, bgColor: 'ECFDF5' },
      { label: 'PAGE VIEWS', value: formatNumber(data.overview?.totalPageviews || 0), color: accentColor, bgColor: 'FDF2F8' },
      { label: 'BOUNCE RATE', value: `${data.overview?.bounceRate || 0}%`, color: cyanColor, bgColor: 'ECFEFF' },
    ];
  }
  return [
    { label: 'UNIQUE VISITORS', value: formatNumber(data.overview?.uniqueVisitors || 0), color: primaryColor, bgColor: 'EEF2FF' },
    { label: 'PAGE VIEWS', value: formatNumber(data.overview?.totalPageviews || 0), color: greenColor, bgColor: 'ECFDF5' },
    { label: 'AD IMPRESSIONS', value: formatNumber(data.overview?.adImpressions || 0), color: accentColor, bgColor: 'FDF2F8' },
    { label: 'AVG CTR', value: `${data.overview?.ctr || 0}%`, color: cyanColor, bgColor: 'ECFEFF' },
  ];
};

const buildInsights = (data, pageType) => {
  const insights = [];
  if (pageType === 'reports') {
    if (data.overview?.totalImpressions) {
      insights.push(`Total of ${formatNumber(data.overview.totalImpressions)} impressions generated ${formatNumber(data.overview.totalClicks || 0)} clicks`);
    }
    if (data.campaignData?.[0]) {
      insights.push(`Top performer: "${data.campaignData[0].name}" with ${data.campaignData[0].ctr || 0}% CTR`);
    }
    if (data.zoneData?.[0]) {
      insights.push(`Best zone: "${data.zoneData[0].name}" delivering ${formatNumber(data.zoneData[0].impressions || 0)} impressions`);
    }
  } else if (pageType === 'analytics') {
    if (data.overview?.uniqueVisitors) {
      insights.push(`${formatNumber(data.overview.uniqueVisitors)} unique visitors with ${formatNumber(data.overview.totalPageviews || 0)} page views`);
    }
    if (data.overview?.bounceRate !== undefined) {
      insights.push(`Bounce rate of ${data.overview.bounceRate}% indicates ${data.overview.bounceRate < 50 ? 'strong' : 'moderate'} engagement`);
    }
    if (data.pagesData?.[0]) {
      insights.push(`Top page: "${data.pagesData[0].path}" with ${formatNumber(data.pagesData[0].views || 0)} views`);
    }
  } else {
    if (data.overview?.uniqueVisitors) {
      insights.push(`${formatNumber(data.overview.uniqueVisitors)} unique visitors tracked this period`);
    }
    if (data.overview?.adImpressions) {
      insights.push(`${formatNumber(data.overview.adImpressions)} ad impressions with ${data.overview.ctr || 0}% CTR`);
    }
  }
  return insights;
};

const buildTopInsight = (data, pageType) => {
  if (pageType === 'reports' && data.campaignData?.[0]) {
    return `Top performer "${data.campaignData[0].name}" achieved ${data.campaignData[0].ctr || 0}% CTR with ${formatNumber(data.campaignData[0].impressions || 0)} impressions`;
  }
  if (pageType === 'analytics' && data.overview?.uniqueVisitors) {
    return `${formatNumber(data.overview.uniqueVisitors)} unique visitors with ${data.overview.bounceRate || 0}% bounce rate this period`;
  }
  if (data.overview?.uniqueVisitors) {
    return `${formatNumber(data.overview.uniqueVisitors)} visitors generated ${formatNumber(data.overview.totalPageviews || 0)} page views`;
  }
  return 'Performance data will appear here once tracking is active';
};

const lightenColor = (hex) => {
  // Simple lightening by mixing with white
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const factor = 0.3;
  const newR = Math.round(r + (255 - r) * factor).toString(16).padStart(2, '0');
  const newG = Math.round(g + (255 - g) * factor).toString(16).padStart(2, '0');
  const newB = Math.round(b + (255 - b) * factor).toString(16).padStart(2, '0');
  return `${newR}${newG}${newB}`.toUpperCase();
};
